import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from './components/ThemeProvider';
import ChatWidget from './components/ChatWidget';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import RestaurantDashboard from './pages/RestaurantDashboard';
import OrderDrivers from './pages/OrderDrivers';
import CreateDelivery from './pages/CreateDelivery';
import DriverPanel from './pages/DriverPanel';
import AdminDashboard from './pages/AdminDashboard';
import BillingPage from './pages/BillingPage';
import FinancialDashboard from './pages/FinancialDashboard';
import PublicPricing from './pages/PublicPricing';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import DriverAgreementPage from './pages/DriverAgreementPage';
import RestaurantAgreementPage from './pages/RestaurantAgreementPage';
import ForgotPassword from './pages/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';
import DeliveryDetail from './pages/DeliveryDetail';

// ─── Auth Context ─────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('starfuel_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem('starfuel_token'));

  function login(userData, authToken) {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('starfuel_user', JSON.stringify(userData));
    localStorage.setItem('starfuel_token', authToken);
  }

  function logout() {
    setUser(null);
    setToken(null);
    localStorage.removeItem('starfuel_user');
    localStorage.removeItem('starfuel_token');
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Route Guards ─────────────────────────────────────────────────────────────
function PrivateRoute({ children, roles }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) {
    // Redirect to correct dashboard based on role
    const dashboards = {
      restaurant: '/dashboard',
      driver: '/driver',
      admin: '/admin',
    };
    return <Navigate to={dashboards[user?.role] || '/login'} replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, isAuthenticated } = useAuth();

  if (isAuthenticated) {
    const dashboards = { restaurant: '/dashboard', driver: '/driver', admin: '/admin' };
    return <Navigate to={dashboards[user.role] || '/dashboard'} replace />;
  }

  return children;
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<PublicRoute><PublicPricing /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/delivery/:id" element={<PrivateRoute roles={['restaurant','admin']}><DeliveryDetail /></PrivateRoute>} />
            <Route path="/pricing" element={<PublicPricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/driver-agreement" element={<DriverAgreementPage />} />
            <Route path="/restaurant-agreement" element={<RestaurantAgreementPage />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

            {/* Restaurant */}
            <Route path="/dashboard" element={
              <PrivateRoute roles={['restaurant']}>
                <RestaurantDashboard />
              </PrivateRoute>
            } />
            <Route path="/order-drivers" element={
              <PrivateRoute roles={['restaurant']}>
                <OrderDrivers />
              </PrivateRoute>
            } />
            <Route path="/create-delivery" element={
              <PrivateRoute roles={['restaurant']}>
                <CreateDelivery />
              </PrivateRoute>
            } />
            <Route path="/billing" element={
              <PrivateRoute roles={['restaurant']}>
                <BillingPage />
              </PrivateRoute>
            } />

            {/* Driver */}
            <Route path="/driver" element={
              <PrivateRoute roles={['driver']}>
                <DriverPanel />
              </PrivateRoute>
            } />

            {/* Admin */}
            <Route path="/finance" element={
              <PrivateRoute roles={['admin']}>
                <FinancialDashboard />
              </PrivateRoute>
            } />
            <Route path="/admin" element={
              <PrivateRoute roles={['admin']}>
                <AdminDashboard />
              </PrivateRoute>
            } />

            {/* 404 */}
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center bg-black text-white">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-yellow-400 mb-4" style={{ fontFamily: 'Bebas Neue' }}>404</h1>
                  <p className="text-gray-400 mb-6">Page not found</p>
                  <a href="/login" className="text-yellow-400 hover:underline">Go home</a>
                </div>
              </div>
            } />
          </Routes>
        <ChatWidget />
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}
